import React, { useState, useEffect } from 'react';
import { playersAPI, positionsAPI, playertoolsAPI, teamsAPI, projectsAPI, getImageUrl } from '../../services/api';

const PlayersSettings = () => {
  const [players, setPlayers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [playerAssignments, setPlayerAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [assigningPlayer, setAssigningPlayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    pageSize: 20
  });

  const [selectedFiles, setSelectedFiles] = useState({
    portrait: null,
    portrait2: null,
    action: null
  });
  const [formData, setFormData] = useState({
    surname: '',
    prename: '',
    posid: '',
    state: true,
    birthday: '',
    height: '',
    weight: '',
    clubSince: '',
    clubUntil: '',
    country: '',
    biography: '',
    misc: {
      portrait: '',
      portrait2: '',
      action: ''
    }
  });
  const [assignData, setAssignData] = useState({
    pid: '',
    tid: '',
    num: '',
    published: true
  });
  useEffect(() => {
    fetchPlayers(0, 20);
    fetchPositions();
    fetchTeams();
    fetchProjects();
  }, []);
  useEffect(() => {
    if (players.length > 0) {
      fetchPlayerAssignments();
    }
  }, [players]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPlayers(0, pagination.pageSize);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);
  const fetchPlayers = async (page = 0, size = 20) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        size: size
      };
      
      // Add search filters if searchTerm exists
      if (searchTerm.trim()) {
        const searchParts = searchTerm.trim().split(' ');
        if (searchParts.length >= 2) {
          params.prename = searchParts[0];
          params.surname = searchParts.slice(1).join(' ');
        } else {
          params.surname = searchParts[0];
        }
      }
      
      const response = await playersAPI.getAll(params);
      const data = response.data;
      
      setPlayers(data.players || []);
      setPagination({
        currentPage: data.currentPage || 0,
        totalPages: data.totalPages || 0,
        totalItems: data.totalItems || 0,
        pageSize: size
      });
    } catch (err) {
      setError('Failed to fetch players');
      console.error('Error fetching players:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await positionsAPI.getAll();
      setPositions(response.data);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getAll();
      setTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data.projects || response.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };
  const fetchPlayerAssignments = async () => {
    try {
      const response = await playertoolsAPI.getAll();
      const assignments = response.data.playertools || response.data;
      
      // Group assignments by player ID
      const assignmentsByPlayer = {};
      assignments.forEach(assignment => {
        const playerId = assignment.plid?._id;
        if (playerId) {
          if (!assignmentsByPlayer[playerId]) {
            assignmentsByPlayer[playerId] = [];
          }
          assignmentsByPlayer[playerId].push(assignment);
        }
      });
      
      setPlayerAssignments(assignmentsByPlayer);
    } catch (err) {
      console.error('Failed to fetch player assignments:', err);
    }
  };

  const handleFileChange = (fieldName, file) => {
    setSelectedFiles(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let portraitFilename = formData.misc.portrait;
      let portrait2Filename = formData.misc.portrait2;
      let actionFilename = formData.misc.action;

      // Handle file upload first if there are selected files
      if (selectedFiles.portrait || selectedFiles.portrait2 || selectedFiles.action) {
        const uploadFormData = new FormData();
        
        if (selectedFiles.portrait) {
          uploadFormData.append('portrait', selectedFiles.portrait);
        }
        if (selectedFiles.portrait2) {
          uploadFormData.append('portrait2', selectedFiles.portrait2);
        }
        if (selectedFiles.action) {
          uploadFormData.append('action', selectedFiles.action);
        }

        const uploadResponse = await playersAPI.uploadFiles(uploadFormData);
        if (uploadResponse.data.files) {
          if (uploadResponse.data.files.portrait) {
            portraitFilename = uploadResponse.data.files.portrait[0].filename;
          }
          if (uploadResponse.data.files.portrait2) {
            portrait2Filename = uploadResponse.data.files.portrait2[0].filename;
          }
          if (uploadResponse.data.files.action) {
            actionFilename = uploadResponse.data.files.action[0].filename;
          }
        }
      }

      // Prepare the main form data
      const submitData = {
        surname: formData.surname,
        prename: formData.prename,
        posid: formData.posid,
        state: formData.state,
        birthday: formData.birthday,
        height: formData.height,
        weight: formData.weight,
        clubSince: formData.clubSince,
        clubUntil: formData.clubUntil,
        country: formData.country,
        biography: formData.biography,
        portrait: portraitFilename,
        portrait2: portrait2Filename,
        action: actionFilename,
        misc: {
          portrait: portraitFilename,
          portrait2: portrait2Filename,
          action: actionFilename,
          birthday: formData.birthday,
          height: formData.height,
          weight: formData.weight,
          clubSince: formData.clubSince,
          clubUntil: formData.clubUntil,
          country: formData.country,
          biography: formData.biography
        }
      };

      if (editingPlayer) {
        await playersAPI.update(editingPlayer._id, submitData);      } else {
        await playersAPI.create(submitData);
      }
      
      fetchPlayers(pagination.currentPage, pagination.pageSize);
      resetForm();
    } catch (err) {
      console.error('Error saving player:', err);
      setError('Failed to save player');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      try {
        await playersAPI.delete(id);        fetchPlayers(pagination.currentPage, pagination.pageSize);
      } catch (err) {
        setError('Failed to delete player');
      }
    }
  };

  const handleAssignPlayer = (player) => {
    setAssigningPlayer(player);
    setAssignData({
      pid: '',
      tid: '',
      num: '',
      published: true
    });
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        plid: assigningPlayer._id,
        pid: assignData.pid,
        tid: assignData.tid,
        num: parseInt(assignData.num) || 0,
        published: assignData.published
      };

      await playertoolsAPI.create(submitData);
      
      fetchPlayerAssignments();
      resetAssignForm();
      setError('');
    } catch (err) {
      console.error('Error assigning player:', err);
      setError(err.response?.data?.message || 'Failed to assign player');
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (window.confirm('Are you sure you want to remove this assignment?')) {
      try {
        await playertoolsAPI.delete(assignmentId);
        fetchPlayerAssignments();
      } catch (err) {
        setError('Failed to remove assignment');
      }
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingPlayer(null);
    setFormData({
      surname: '',
      prename: '',
      posid: '',
      state: true,
      birthday: '',
      height: '',
      weight: '',
      clubSince: '',
      clubUntil: '',
      country: '',
      biography: '',
      misc: {
        portrait: '',
        portrait2: '',
        action: ''
      }
    });
    setSelectedFiles({ portrait: null, portrait2: null, action: null });
  };

  const resetAssignForm = () => {
    setShowAssignModal(false);
    setAssigningPlayer(null);
    setAssignData({
      pid: '',
      tid: '',
      num: '',
      published: true
    });
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      surname: player.surname,
      prename: player.prename || '',
      posid: player.posid?._id || '',
      state: player.state,
      birthday: player.misc?.birthday || '',
      height: player.misc?.height || '',
      weight: player.misc?.weight || '',
      clubSince: player.misc?.clubSince || '',
      clubUntil: player.misc?.clubUntil || '',
      country: player.misc?.country || '',
      biography: player.misc?.biography || '',
      misc: {
        portrait: player.misc?.portrait || '',
        portrait2: player.misc?.portrait2 || '',
        action: player.misc?.action || ''
      }
    });
    setShowModal(true);
  };
  const handleSearch = () => {
    fetchPlayers(0, pagination.pageSize);
  };

  const handlePageChange = (newPage) => {
    fetchPlayers(newPage, pagination.pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    fetchPlayers(0, newSize);
  };

  // Remove client-side filtering since we're doing server-side search
  const displayPlayers = players;

  if (loading) return <div className="loading loading-spinner loading-lg"></div>;
  return (
    <div className="">
      <div className="">
        {/* Header Section */}
        <div className="mb-12 fade-in">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                Player Management 👥
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                Manage your team's players and their information
              </p>
            </div>
            <button
              className="btn-modern-primary"
              onClick={() => setShowModal(true)}
            >
              <span className="mr-2">➕</span>
              Add New Player
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 slide-up">
            <div className="modern-card p-6 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-3">
                <span className="text-2xl">❌</span>
                <div>
                  <h3 className="font-semibold text-red-800 dark:text-red-200">Error</h3>
                  <p className="text-red-600 dark:text-red-300">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Section */}
        <div className="modern-card p-8 mb-8 slide-up">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-6 flex items-center gap-3">
            <span className="text-2xl">🔍</span>
            Search Players
          </h3>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search by name
              </label>
              <input
                type="text"
                placeholder="Enter first name, last name, or both..."
                className="input-modern"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <button 
              className="btn-modern-primary"
              onClick={handleSearch}
            >
              <span className="mr-2">🔍</span>
              Search
            </button>
            <button 
              className="btn-modern-outline"
              onClick={() => {
                setSearchTerm('');
                fetchPlayers(0, pagination.pageSize);
              }}
            >
              <span className="mr-2">🔄</span>
              Clear
            </button>
          </div>
        </div>

        {/* Players Grid/Table */}
        <div className="modern-card slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-3">
                <span className="text-2xl">👥</span>
                Players ({pagination.totalItems})
              </h3>            
            {/* Players Display */}
            {Array.isArray(displayPlayers) && displayPlayers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {displayPlayers.map((player, index) => (
                  <div 
                    key={player._id} 
                    className="modern-card p-6 hover:scale-105 transition-transform duration-300"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {/* Player Header */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                        {player.misc?.portrait ? (
                          <img 
                            src={getImageUrl(player.misc.portrait)}
                            alt="Portrait"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="w-full h-full flex items-center justify-center text-white font-bold text-lg"
                          style={{ display: player.misc?.portrait ? 'none' : 'flex' }}
                        >
                          {player.surname.charAt(0)}{player.prename?.charAt(0) || ''}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-lg text-gray-800 dark:text-white">
                          {player.prename} {player.surname}
                        </h4>
                        <div className="flex items-center gap-2">
                          {player.posid ? (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-lg text-xs font-medium">
                              {player.posid.name}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">No Position</span>
                          )}
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            player.state 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          }`}>
                            {player.state ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Player Info */}
                    <div className="space-y-3 mb-6">
                      {player.misc?.birthday && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500 dark:text-gray-400">🎂</span>
                          <span className="text-gray-700 dark:text-gray-300">{player.misc.birthday}</span>
                        </div>
                      )}
                      
                      {/* Assignments */}
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-sm block mb-2">Team Assignments:</span>
                        {playerAssignments[player._id] && playerAssignments[player._id].length > 0 ? (
                          <div className="space-y-1">
                            {playerAssignments[player._id].slice(0, 2).map((assignment) => (
                              <div key={assignment._id} className="flex items-center justify-between gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  #{assignment.num || '-'} {assignment.tid?.name}
                                </span>
                                <button
                                  className="text-red-500 hover:text-red-700 text-sm"
                                  onClick={() => handleRemoveAssignment(assignment._id)}
                                  title="Remove assignment"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            {playerAssignments[player._id].length > 2 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                +{playerAssignments[player._id].length - 2} more assignments
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400 text-sm italic">No assignments</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        className="flex-1 btn-modern-primary text-sm py-2"
                        onClick={() => handleAssignPlayer(player)}
                        title="Assign to team"
                      >
                        👥 Assign
                      </button>
                      <button
                        className="btn-modern-outline px-3 py-2 text-sm"
                        onClick={() => handleEdit(player)}
                        title="Edit player"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-modern-outline px-3 py-2 text-sm text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleDelete(player._id)}
                        title="Delete player"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-8xl mb-6">👥</div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                  {searchTerm ? 'No players found' : 'No players yet'}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  {searchTerm 
                    ? 'Try adjusting your search terms or clear the search to see all players.'
                    : 'Add your first player to get started with team management.'
                  }
                </p>
                {!searchTerm && (
                  <button
                    className="btn-modern-primary"
                    onClick={() => setShowModal(true)}
                  >
                    <span className="mr-2">➕</span>
                    Add First Player
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="modern-card p-6 mt-8 slide-up">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-300">Show</span>
                <select 
                  className="input-modern py-2 px-3"
                  value={pagination.pageSize}                  onChange={(e) => {
                    const newSize = parseInt(e.target.value);
                    setPagination(prev => ({ ...prev, pageSize: newSize }));
                    fetchPlayers(0, newSize);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600 dark:text-gray-300">per page</span>
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Showing {pagination.currentPage * pagination.pageSize + 1} to{' '}
                {Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalItems)} of{' '}
                {pagination.totalItems} players
              </div>
              
              <div className="flex gap-2">
                <button 
                  className="btn-modern-outline px-3 py-2 text-sm"
                  disabled={pagination.currentPage === 0}
                  onClick={() => handlePageChange(0)}
                >
                  ««
                </button>
                <button 
                  className="btn-modern-outline px-3 py-2 text-sm"
                  disabled={pagination.currentPage === 0}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                >
                  «
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i;
                  } else if (pagination.currentPage <= 2) {
                    pageNum = i;
                  } else if (pagination.currentPage >= pagination.totalPages - 3) {
                    pageNum = pagination.totalPages - 5 + i;
                  } else {
                    pageNum = pagination.currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                        pagination.currentPage === pageNum 
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                          : 'btn-modern-outline'
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
                
                <button 
                  className="btn-modern-outline px-3 py-2 text-sm"                  disabled={pagination.currentPage >= pagination.totalPages - 1}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                >
                  »
                </button>
                <button 
                  className="btn-modern-outline px-3 py-2 text-sm"
                  disabled={pagination.currentPage >= pagination.totalPages - 1}
                  onClick={() => handlePageChange(pagination.totalPages - 1)}
                >
                  »»
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

      {/* Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box w-11/12 max-w-4xl">
            <h3 className="font-bold text-lg mb-4">
              {editingPlayer ? 'Edit Player' : 'Add New Player'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Image Uploads */}
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Portrait Upload */}
                  <div>
                    <label className="label">
                      <span className="label-text">Portrait</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      className="file-input file-input-bordered w-full"
                      onChange={(e) => handleFileChange('portrait', e.target.files[0])}
                    />
                    {formData.misc.portrait && (
                      <div className="mt-2">
                        <img 
                          src={getImageUrl(formData.misc.portrait)}
                          alt="Portrait preview"
                          className="w-20 h-20 object-cover rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div 
                          className="text-red-500 text-sm mt-1"
                          style={{ display: 'none' }}
                        >
                          Image not found: {formData.misc.portrait}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Portrait2 Upload */}
                  <div>
                    <label className="label">
                      <span className="label-text">Portrait 2</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      className="file-input file-input-bordered w-full"
                      onChange={(e) => handleFileChange('portrait2', e.target.files[0])}
                    />
                    {formData.misc.portrait2 && (
                      <div className="mt-2">
                        <img 
                          src={getImageUrl(formData.misc.portrait2)}
                          alt="Portrait 2 preview"
                          className="w-20 h-20 object-cover rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div 
                          className="text-red-500 text-sm mt-1"
                          style={{ display: 'none' }}
                        >
                          Image not found: {formData.misc.portrait2}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Image Upload */}
                  <div>
                    <label className="label">
                      <span className="label-text">Action Image</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      className="file-input file-input-bordered w-full"
                      onChange={(e) => handleFileChange('action', e.target.files[0])}
                    />
                    {formData.misc.action && (
                      <div className="mt-2">
                        <img 
                          src={getImageUrl(formData.misc.action)}
                          alt="Action image preview"
                          className="w-20 h-20 object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div 
                          className="text-red-500 text-sm mt-1"
                          style={{ display: 'none' }}
                        >
                          Image not found: {formData.misc.action}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Surname */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Surname *</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Prename */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Prename</span>
                  </label>
                  <input
                    type="text"
                    value={formData.prename}
                    onChange={(e) => setFormData({...formData, prename: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Position */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Position</span>
                  </label>
                  <select
                    value={formData.posid}
                    onChange={(e) => setFormData({...formData, posid: e.target.value})}
                    className="select select-bordered"
                  >
                    <option value="">Select Position</option>
                    {positions.map((position) => (
                      <option key={position._id} value={position._id}>
                        {position.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Birthday */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Birthday</span>
                  </label>
                  <input
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => setFormData({...formData, birthday: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Height */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Height (cm)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.height}
                    onChange={(e) => setFormData({...formData, height: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Weight */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Weight (kg)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* At the club since */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">At the club since</span>
                  </label>
                  <input
                    type="date"
                    value={formData.clubSince}
                    onChange={(e) => setFormData({...formData, clubSince: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* At the club until */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">At the club until</span>
                  </label>
                  <input
                    type="date"
                    value={formData.clubUntil}
                    onChange={(e) => setFormData({...formData, clubUntil: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Country */}
                <div className="form-control col-span-full">
                  <label className="label">
                    <span className="label-text">Country</span>
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="input input-bordered"
                  />
                </div>

                {/* Biography */}
                <div className="form-control col-span-full">
                  <label className="label">
                    <span className="label-text">Biography</span>
                  </label>
                  <textarea
                    value={formData.biography}
                    onChange={(e) => setFormData({...formData, biography: e.target.value})}
                    className="textarea textarea-bordered h-32"
                    placeholder="Enter player biography..."
                  />
                </div>

                {/* Active */}
                <div className="form-control">
                  <label className="label cursor-pointer">
                    <span className="label-text">Active</span>
                    <input
                      type="checkbox"
                      checked={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.checked})}
                      className="checkbox"
                    />
                  </label>
                </div>
              </div>

              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingPlayer ? 'Update' : 'Create'} Player
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}

      {/* Assign Modal */}
      {showAssignModal && assigningPlayer && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              Assign {assigningPlayer.surname}, {assigningPlayer.prename} to Team
            </h3>
            
            <form onSubmit={handleAssignSubmit} className="space-y-4">
              {/* Project */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Project *</span>
                </label>
                <select
                  required
                  value={assignData.pid}
                  onChange={(e) => setAssignData({...assignData, pid: e.target.value, num: ''})}
                  className="select select-bordered"
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Team */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Team *</span>
                </label>
                <select
                  required
                  value={assignData.tid}
                  onChange={(e) => setAssignData({...assignData, tid: e.target.value, num: ''})}
                  className="select select-bordered"
                >
                  <option value="">Select Team</option>
                  {teams.map((team) => (
                    <option key={team._id} value={team._id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>              {/* Jersey Number */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Jersey Number</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={assignData.num}
                  onChange={(e) => setAssignData({...assignData, num: e.target.value})}
                  className="input input-bordered"
                  placeholder="Enter jersey number"
                />
              </div>

              {/* Published */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Active Assignment</span>
                  <input
                    type="checkbox"
                    checked={assignData.published}
                    onChange={(e) => setAssignData({...assignData, published: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>

              <div className="modal-action">                <button type="submit" className="btn btn-primary">
                  Assign Player
                </button>
                <button type="button" className="btn" onClick={resetAssignForm}>
                  Cancel                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default PlayersSettings;
